// React component — the canonical drop-in for rendering a pattern
// manifest at display time. Wraps a `<canvas>` with DPR-aware sizing,
// loading state, and error handling.
//
// For OptimizedImage integration: compose this inside the existing
// component, render-on-top-of or alongside the underlying image element.
// For consumers that need a URL (e.g. Next.js Image), use
// `renderManifestToBlob` and keep Blob URL creation and cleanup at the callsite.

'use client';

import { useRef } from 'react';
import type { ManifestFallbackEvent, PatternManifest } from '../manifest';
import { usePatternCanvas } from './use-pattern-canvas';

export interface PatternCanvasProps {
  /** The manifest to render. `null` puts the canvas into idle (blank) state. */
  readonly manifest: PatternManifest | null;
  /** CSS pixel width. The bitmap is rendered at `width × devicePixelRatio`. */
  readonly width: number;
  /** CSS pixel height. The bitmap is rendered at `height × devicePixelRatio`. */
  readonly height: number;
  /** Forwarded to the `<canvas>`. */
  readonly className?: string | undefined;
  /** Override `devicePixelRatio`. Defaults to `window.devicePixelRatio`. */
  readonly dpr?: number | undefined;
  /** Render error callback. */
  // ALLOW-UNKNOWN: generic error callback — caller narrows in their own handler
  readonly onError?: ((err: unknown) => void) | undefined;
  /** Lib-fallback telemetry — fires when a manifest ref fails to resolve. */
  readonly onFallback?: ((event: ManifestFallbackEvent) => void) | undefined;
  /** ARIA label for the canvas. */
  readonly ariaLabel?: string | undefined;
}

export function PatternCanvas({
  manifest,
  width,
  height,
  className,
  dpr,
  onError,
  onFallback,
  ariaLabel,
}: PatternCanvasProps): React.ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const { state } = usePatternCanvas(canvasRef, manifest, {
    width,
    height,
    ...(dpr !== undefined ? { dpr } : {}),
    ...(onError !== undefined ? { onError } : {}),
    ...(onFallback !== undefined ? { onFallback } : {}),
  });

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      data-state={state}
      style={{ width: `${width}px`, height: `${height}px` }}
      role="img"
      aria-label={ariaLabel}
    />
  );
}
